import { Box, Text, Flex, Image } from "rebass/styled-components";
import styled from "styled-components";
import { useRecoilState, useSetRecoilState } from "recoil";

import { PlaylistClip } from "MVVM/Views/EmbeddablePlaylist/@types";
import {
  ActivePlaylistClipState,
  FallbackPlaylistTypeState,
  WatchedClipsCountState,
} from "MVVM/State/embeddablePlaylist";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { formatPlaylistClip, getPlayerName } from "../../../../Utilities";

const TeamPlayerOverlap = ({
  teamLogo,
  playerPhoto,
}: {
  teamLogo?: string;
  playerPhoto?: string;
}) => {
  return (
    <OverlapContainer>
      {teamLogo && <TeamLogo src={teamLogo} alt="Team" />}
      {playerPhoto && <PlayerPhoto src={playerPhoto} alt="Player" />}
    </OverlapContainer>
  );
};

export const MoreClipsColumn = ({
  clips,
  onWatchLimitReached,
}: {
  clips: PlaylistClip[];
  onWatchLimitReached?: (playerName: string, clickedClip: PlaylistClip) => void;
}) => {
  const [activePlaylistClip, setActivePlaylistClip] = useRecoilState(
    ActivePlaylistClipState,
  );
  const [watchedClipsCount, setWatchedClipsCount] = useRecoilState(
    WatchedClipsCountState,
  );
  const setFallbackPlaylistType = useSetRecoilState(FallbackPlaylistTypeState);
  const { track } = useAnalyticsContext();

  const handleClipClick = (clip: PlaylistClip) => {
    if (watchedClipsCount >= 2 && onWatchLimitReached) {
      const playerName = getPlayerName({
        metadata: clip.metadata.map((m) => ({
          key: String(m.key || ""),
          value: String(m.value || ""),
        })),
      });
      onWatchLimitReached(playerName || clip.username || "", clip);
      return;
    }

    const formattedClip = formatPlaylistClip(clip);

    track("Button - Click", {
      locationIdentifier: "thumbnail_click",
      location: "embeddable_playlist",
    });
    setFallbackPlaylistType("");
    setActivePlaylistClip(formattedClip);
    setWatchedClipsCount((prev) => prev + 1);
  };
  return (
    <ScrollableContainer>
      {clips.map((clip) => {
        const teamLogo = clip.metadata.find((meta) => meta.key === "teamLogo");
        const playerPhoto = clip.metadata.find(
          (meta) => meta.key === "playerPhoto",
        );
        const isActive = activePlaylistClip
          ? activePlaylistClip.internalId === clip.internalId
          : false;

        return (
          <ClipRow
            key={clip._id}
            alignItems="center"
            onClick={() => handleClipClick(clip)}
            $isActive={isActive}
          >
            <ThumbnailContainer>
              <Image
                src={
                  clip.mobile
                    ? clip.mobile?.thumbnailStandardUrl
                    : clip.thumbnailStandardUrl
                }
                alt={clip.title}
                width="100%"
                height="100%"
                style={{ objectFit: "cover" }}
              />
            </ThumbnailContainer>

            <ClipInfo>
              <UsernameRow>
                <TeamPlayerOverlap
                  teamLogo={teamLogo?.value as string}
                  playerPhoto={playerPhoto?.value as string}
                />
                <ClipUsername>{clip.username}</ClipUsername>
              </UsernameRow>
              <ClipTitle>{clip.title}</ClipTitle>
            </ClipInfo>
          </ClipRow>
        );
      })}
    </ScrollableContainer>
  );
};

const ScrollableContainer = styled(Flex)`
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  flex-direction: column;
  width: 100%;
  height: 85%;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }

  @media (max-width: 480px) {
    &::-webkit-scrollbar {
      width: 2px;
    }
  }
`;

const ClipRow = styled(Flex)<{ $isActive: boolean }>`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  border-left: ${({ $isActive }) =>
    $isActive ? "3px solid #4DBD94" : "3px solid transparent"};
  cursor: pointer;
  padding: 12px 16px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
  }

  @media (max-width: 360px) {
    padding: 8px 10px;
  }
`;

const ThumbnailContainer = styled(Box)`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  width: 40px;
  aspect-ratio: 9/16;

  @media (max-width: 480px) {
    width: 30px;
  }

  @media (max-width: 360px) {
    width: 30px;
  }
`;

const ClipInfo = styled(Box)`
  flex: 1;
  margin-left: 12px;
  min-width: 0;

  @media (max-width: 480px) {
    margin-left: 10px;
  }

  @media (max-width: 360px) {
    margin-left: 8px;
  }
`;

const UsernameRow = styled(Flex)`
  align-items: center;
  margin-bottom: 6px;

  @media (max-width: 360px) {
    margin-bottom: 4px;
  }
`;

const ClipTitle = styled(Text)`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: white;
  display: -webkit-box;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  overflow: hidden;

  @media (max-width: 480px) {
    font-size: 11px;
    line-height: 1.2;
  }

  @media (max-width: 360px) {
    -webkit-line-clamp: 1;
    font-size: 10px;
    line-height: 1.1;
  }
`;

const ClipUsername = styled(Text)`
  color: white;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  margin-left: 4px;

  @media (max-width: 480px) {
    font-size: 14px;
  }

  @media (max-width: 360px) {
    font-size: 12px;
    margin-left: 2px;
  }
`;

const OverlapContainer = styled(Flex)`
  position: relative;
  align-items: center;
`;

const TeamLogo = styled(Image)`
  border-radius: 50%;
  height: 24px;
  width: 24px;

  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 360px) {
    width: 18px;
    height: 18px;
  }
`;

const PlayerPhoto = styled(Image)`
  border-radius: 50%;
  height: 24px;
  margin-left: -15px;
  width: 24px;
  z-index: 1;

  @media (max-width: 480px) {
    height: 20px;
    margin-left: -12px;
    width: 20px;
  }

  @media (max-width: 360px) {
    height: 18px;
    margin-left: -10px;
    width: 18px;
  }
`;
